import React from 'react';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { 
            hasError: false, 
            error: null, 
            errorInfo: null 
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Log error details
        this.setState({
            error: error,
            errorInfo: errorInfo
        });

        // Log to console in development
        if (process.env.NODE_ENV === 'development') {
            console.error('ErrorBoundary caught an error:', error, errorInfo);
        }
    }

    handleRetry = () => {
        this.setState({ 
            hasError: false, 
            error: null, 
            errorInfo: null 
        });
    };

    render() {
        if (this.state.hasError) {
            // Custom error UI
            return (
                <div 
                    className="min-h-screen flex items-center justify-center bg-secondary-50 px-4"
                    role="alert"
                    aria-live="assertive"
                >
                    <div className="max-w-md w-full bg-white rounded-lg shadow-medium p-6 text-center">
                        <div className="mb-4">
                            <svg 
                                className="mx-auto h-12 w-12 text-red-500" 
                                fill="none" 
                                viewBox="0 0 24 24" 
                                stroke="currentColor"
                                aria-hidden="true"
                            >
                                <path 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                    strokeWidth={2} 
                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" 
                                />
                            </svg>
                        </div>
                        
                        <h1 className="text-xl font-semibold text-secondary-900 mb-2">
                            Something went wrong
                        </h1>
                        
                        <p className="text-secondary-600 mb-6">
                            We apologize for the inconvenience. An unexpected error occurred while loading this page.
                        </p>
                        
                        <div className="space-y-3">
                            <button
                                onClick={this.handleRetry}
                                className="w-full bg-primary-500 text-white px-4 py-2 rounded-md 
                                         hover:bg-primary-600 focus:outline-none focus:ring-2 
                                         focus:ring-primary-500 focus:ring-offset-2 
                                         transition-colors duration-200"
                            >
                                Try Again
                            </button>
                            
                            <button
                                onClick={() => window.location.href = '/'}
                                className="w-full bg-secondary-100 text-secondary-700 px-4 py-2 rounded-md 
                                         hover:bg-secondary-200 focus:outline-none focus:ring-2 
                                         focus:ring-secondary-500 focus:ring-offset-2 
                                         transition-colors duration-200"
                            >
                                Go to Homepage
                            </button>
                        </div>
                        
                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details className="mt-6 text-left">
                                <summary className="cursor-pointer text-sm text-secondary-500 hover:text-secondary-700">
                                    Error Details (Development)
                                </summary>
                                <div className="mt-2 p-3 bg-red-50 rounded text-xs text-red-700 overflow-auto">
                                    <pre>{this.state.error.toString()}</pre>
                                    <pre>{this.state.errorInfo.componentStack}</pre>
                                </div>
                            </details>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

// Higher-order component for easier usage
export const withErrorBoundary = (Component, errorFallback) => {
    return function WrappedComponent(props) {
        return (
            <ErrorBoundary fallback={errorFallback}>
                <Component {...props} />
            </ErrorBoundary>
        );
    };
};

export default ErrorBoundary;